import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, Loader2, Send, Upload, X } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { validerFichier } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   SIGNALER UN PROBLÈME · COMMENTAIRES ET SUGGESTIONS

   Un seul composant pour les deux formulaires : ils diffèrent par leur liste
   de catégories et par le ton, pas par leur mécanique. En écrire deux, c'était
   accepter qu'ils divergent à la première retouche.

   Deux choix qui comptent :

   1. LES CATÉGORIES VIENNENT DU SERVEUR. Les recopier ici aurait créé une
      seconde liste : le jour où l'on ajoute « Lenteur », il faudrait y penser
      à deux endroits, et le serveur refuserait la valeur que l'interface
      propose.

   2. LA PAGE EST JOINTE SANS ÊTRE DEMANDÉE. « Ça ne marche pas » sans savoir
      où oblige à répondre « vous étiez où, exactement ? » — et la moitié des
      gens ne répondent jamais. L'adresse courante part avec le message ; le
      navigateur est relevé côté serveur.
   ═══════════════════════════════════════════════════════════════════════════ */

const TEXTES = {
  probleme: {
    titre: 'Signaler un problème',
    intro: "Décrivez ce qui s'est passé. Plus vous êtes précis, plus vite nous pourrons corriger.",
    libelleCategorie: 'De quel type de problème s\'agit-il ?',
    libelleMessage: 'Que s\'est-il passé ?',
    aideMessage: "Ce que vous faisiez, ce que vous attendiez, ce qui est arrivé à la place.",
    bouton: 'Envoyer le signalement',
    succes: "Merci. Votre signalement est enregistré avec la page concernée ; nous revenons vers vous si besoin.",
    avecCapture: true,
  },
  suggestion: {
    titre: 'Commentaires et suggestions',
    intro: "Ce qui vous manque, ce qui vous ralentit, ce qui mériterait d'exister.",
    libelleCategorie: 'De quoi souhaitez-vous nous parler ?',
    libelleMessage: 'Votre commentaire',
    aideMessage: "Dites-le simplement ; nous lisons tout.",
    bouton: 'Envoyer',
    succes: "Merci pour votre retour. Il est transmis à l'équipe.",
    avecCapture: false,
  },
};

export default function FormulaireRetour({ nature = 'probleme', onFermer }) {
  const location = useLocation();
  const config = TEXTES[nature] || TEXTES.probleme;

  // `null` tant que la liste n'est pas arrivée.
  const [categories, setCategories] = useState(null);
  const [erreurCategories, setErreurCategories] = useState(false);
  const [categorie, setCategorie] = useState('');
  const [message, setMessage] = useState('');
  const [capture, setCapture] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  const charger = useCallback(async () => {
    setErreurCategories(false);
    try {
      const d = await adminApi.categoriesRetour();
      setCategories(d?.[nature] || []);
    } catch {
      setErreurCategories(true);
    }
  }, [nature]);

  useEffect(() => { charger(); }, [charger]);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur('');
    if (!categorie) { setErreur('Choisissez une catégorie.'); return; }
    if (!message.trim()) { setErreur('Le message est requis.'); return; }
    setEnvoi(true);
    try {
      await adminApi.envoyerRetour({
        nature, categorie, message: message.trim(),
        page: location.pathname, capture,
      });
      setSucces(config.succes);
      setMessage('');
      setCategorie('');
      setCapture(null);
    } catch (err) {
      setErreur(err.message || "Votre retour n'a pas pu être envoyé.");
    } finally {
      setEnvoi(false);
    }
  };

  if (succes) {
    return (
      <div className="rounded-pastille border border-[var(--accent)]/30 bg-[var(--accent)]/8 p-6 text-center">
        <span className="inline-flex w-10 h-10 rounded-controle items-center justify-center"
              style={{ background: 'var(--accent)' }}>
          <Check className="w-5 h-5" aria-hidden="true" style={{ color: '#0A1128' }} />
        </span>
        <p className="text-[14px] text-[var(--text-main)] leading-relaxed mt-3 max-w-[48ch] mx-auto">
          {succes}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={() => setSucces('')} className="act act-contour">
            Envoyer un autre retour
          </button>
          {onFermer && (
            <button type="button" onClick={onFermer} className="act">Fermer</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={soumettre} className="space-y-4">
      <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">{config.intro}</p>

      <div>
        <label htmlFor={`cat-${nature}`}
               className="block text-[13px] font-medium text-[var(--text-main)] mb-1.5">
          {config.libelleCategorie}
        </label>
        <select id={`cat-${nature}`} value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
                disabled={categories === null}
                aria-busy={(categories === null && !erreurCategories) || undefined}
                style={{ minHeight: 44 }}
                className="champ">
          <option value="">
            {categories === null ? (erreurCategories ? 'Catégories indisponibles' : 'Chargement des catégories…') : 'Choisissez…'}
          </option>
          {(categories || []).map((c) => (
            <option key={c.code} value={c.code}>{c.libelle}</option>
          ))}
        </select>
        {erreurCategories && (
          <p role="alert" className="text-[12.5px] text-[var(--alerte)] mt-1.5">
            Les catégories n’ont pas pu être chargées.{' '}
            <button type="button" onClick={charger} className="font-semibold underline underline-offset-2">
              Réessayer
            </button>
          </p>
        )}
      </div>

      <div>
        <label htmlFor={`msg-${nature}`}
               className="block text-[13px] font-medium text-[var(--text-main)] mb-1.5">
          {config.libelleMessage}
        </label>
        <textarea id={`msg-${nature}`} rows={5} value={message} maxLength={5000}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={config.aideMessage}
                  className="champ resize-y" />
      </div>

      {/* La capture n'a de sens que pour un problème : montrer l'écran fautif
          vaut dix lignes de description. Une suggestion, elle, se raconte. */}
      {config.avecCapture && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold
                            text-[var(--text-muted)] hover:text-[var(--text-bold)]
                            cursor-pointer transition-colors">
            <Upload className="w-3.5 h-3.5" aria-hidden="true" />
            {capture ? 'Changer la capture' : "Joindre une capture d'écran"}
            <input type="file" accept="image/*" className="sr-only"
                   onChange={(e) => {
                     const fichier = e.target.files?.[0] || null;
                     e.target.value = '';
                     if (!fichier) return;
                     // Vérifiée avant l'envoi : un format refusé se dit ici,
                     // pas après avoir tout rédigé.
                     const probleme = validerFichier(fichier, { extensions: ['.png', '.jpg', '.jpeg', '.gif', '.webp'] });
                     setErreur(probleme || '');
                     if (!probleme) setCapture(fichier);
                   }} />
          </label>
          {capture && (
            <span className="inline-flex items-center gap-1.5 min-w-0 px-2 py-1 rounded-pastille
                             bg-[var(--bg-main)] text-[11.5px] text-[var(--text-main)]">
              <span className="truncate max-w-[14rem]">{capture.name}</span>
              <button type="button" onClick={() => setCapture(null)}
                      aria-label="Retirer la capture"
                      className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-bold)]">
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </span>
          )}
        </div>
      )}

      <p className="text-[11.5px] text-[var(--text-muted)]">
        La page d'où vous écrivez ({location.pathname}) est jointe automatiquement.
      </p>

      {erreur && (
        <p role="alert" className="text-[13px] text-[var(--alerte)]">{erreur}</p>
      )}

      <button type="submit" disabled={envoi} aria-busy={envoi || undefined}
              style={{ minHeight: 44, backgroundColor: 'var(--accent)', color: '#0A1128' }}
              className="act act-tertiaire">
        {envoi ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
               : <Send className="w-4 h-4" aria-hidden="true" />}
        {envoi ? 'Envoi…' : config.bouton}
      </button>
    </form>
  );
}
